import { useMutation, useQuery } from '@tanstack/react-query';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { useSelector } from '@/redux/helper';
import { useCurrentStreamEvent } from '@/routes/habitat/hooks';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';

interface SingleHabitatDayPassProps {
  habitatId: string;
  entryPoint?: string;
}

const SingleHabitatDayPass = ({ habitatId, entryPoint }: SingleHabitatDayPassProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((habitat) => habitat._id === habitatId));
  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey: 'single-habitat-day-pass',
    }),
    select: (data) => data.list[0],
  });

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const [schedule, now] = useCurrentStreamEvent(habitat?._id);

  if (!habitat) return null;

  const isOnline = habitat.isStreamOn;
  const isEndOfDay =
    isOnline && schedule && Date.parse(schedule.stopTime) < now + 3_600_000 && now > Date.parse(schedule.startTime);
  const isAsleep = !isOnline && !!schedule;
  const isOffline = !isOnline && !schedule;

  return (
    <div className="flex w-full max-w-[336px] flex-col items-center justify-center gap-3 rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div className="relative h-[104px] w-full rounded-t-2xl bg-gray-400">
        {habitat.backgroundImage && (
          <img
            src={habitat.backgroundImage}
            alt={habitat.title}
            className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
          />
        )}
        {isOnline && !isEndOfDay && (
          <span className="absolute right-3 top-2 flex rounded-md bg-olive-3 px-2 py-1 text-[10px]/none font-bold text-hunter-green-3">
            Online
          </span>
        )}
        <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
          <div className="relative">
            {habitat.profileImage ? (
              <img
                src={habitat.profileImage}
                alt={habitat.title}
                className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
              />
            ) : (
              <div
                data-available={!isOffline}
                className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white"
              >
                {habitat.title.charAt(0)}
              </div>
            )}
            {isOffline && <div className="absolute inset-0 rounded-full bg-white/50" />}
            {habitat.zoo && (
              <div className="absolute bottom-0 right-0 rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                <img
                  src={habitat.zoo.logo}
                  alt={habitat.zoo.name}
                  className="preflight preflight-img size-[44px] overflow-hidden rounded-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <h4 className="preflight preflight-h4 mx-4 max-w-[304px] pt-7 text-center text-2xl/7 font-bold text-green-900">
        Single Day Pass
      </h4>
      {isEndOfDay ? (
        <span className="flex rounded-md bg-rose-700 px-2 py-1 text-[10px]/none font-bold text-white">End of Day</span>
      ) : isAsleep ? (
        <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Asleep</span>
      ) : isOffline ? (
        <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Offline</span>
      ) : null}
      <p className="preflight preflight-p mx-4 max-w-[274px] text-center text-base/[18px] text-gray-500">
        24h Access to One Habitat.
        <br />
        You Selected <strong>{habitat.title}</strong>
      </p>
      <ProductPrice currency={product?.price.currency} price={product?.price.price} className="px-4" />
      <div className="w-full px-4 pb-4">
        <Button
          disabled={isOffline || isPending || !product?.eligibility?.allowed}
          size="manual"
          className="w-full px-4 py-3 text-base/none font-bold"
          onClick={() => {
            if (!product) return;
            checkout({
              productId: product._id,
              priceId: product.price.priceId,
              associatedHabitatIds: [habitat._id],
              successPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
              cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
            });
            trackMP('request-checkout', {
              productId: product._id,
              priceId: product.price.priceId,
              productLookupKey: product.productLookupKey,
              habitatId: habitat._id,
              entryPoint,
            });
          }}
        >
          {isOffline ? 'Unavailable' : 'Select Pass'}
        </Button>
        {product && <ProductEligibilityResult result={product.eligibility} className="mt-3" />}
      </div>
    </div>
  );
};

export default SingleHabitatDayPass;
